import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Box } from "./Layout/Box";
import { Content } from "./Layout/Content";
import { breakpoints, Media } from "../styles";
import { Modal } from "../components/Modal/Modal";
import { useAccount } from "wagmi";
import { Contract } from "ethers/lib/ethers";
import { formatUnits } from "@ethersproject/units";
import { BigNumber } from "@ethersproject/bignumber";
import toast, { Toaster } from "react-hot-toast";
import { useWidthBreakpoint } from "../hooks";
import { CollabAirdropABI } from "../constants/ABI/CollabAirdropABI";
import { PointsIcon } from "./Icons/PointsIcon";
import { Accordion } from "./Accordion/Accordion";
import Confetti from "react-confetti";
import collabIcon from '../assets/coins/COLLAB.svg'
import { formatDisplay, formatDisplayShorthand } from "../utils/formatDisplay";
import { COLLAB_DROP_API } from "../constants/urls";
import { useEthersProvider, useEthersSigner } from '@/utils/wagmi-ethers-adapters/ethers';

export type ProofData = {
  merkleRoot: string;
  tokenTotal: string;
  claims: ClaimData;
};

export type DistributionAmount = {
  [address: string]: number;
};

export type DistributionAmountData = {
  time: string;
  tokenDistribution: DistributionAmount;
};

type ClaimData = {
  index: number;
  amount: string;
  proof: string[];
  distribution: number[];
};

export const CollabAirdropBanner = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [claimData, setClaimData] = useState<ClaimData | null>(null);
  const [isClaimed, setIsClaimed] = useState<boolean>(true);
  const [showConfetti, setShowConfetti] = React.useState(false);
  const [claimDisabled, setClaimDisabled] = useState(false)

  const { address : walletAddress, isConnected } = useAccount();
  
  const { data: signer } = useEthersSigner();
  const provider = useEthersProvider();

  const contract = new Contract("0x5A5B14594f1BF93656a2B705ECdb873373A4eFe9", CollabAirdropABI, signer || provider);

  const isMobile = useWidthBreakpoint(breakpoints.mobile);

  useEffect(() => {
    isConnected && updateClaimData()
  }, [walletAddress, isConnected]);

  useEffect(() => {
    checkUserClaimStatus();
  }, [claimData]);

  const handleOpenModal = () => {
    setModalOpen(true);
    setShowConfetti(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setShowConfetti(false);
  };

  const handleClaim = async () => {
    if (!contract || !claimData) return;
    try {
      const indexToPass = BigNumber.from(claimData.index);
      const addressToPass =
        walletAddress || "0x0000000000000000000000000000000000000000";
      const amountToPass = BigNumber.from(claimData.amount);
      const formattedProof = claimData.proof.map(
        (proofElement) =>
          `0x${proofElement.replace(/^0x/, "")}` as `0x${string}`
      );

      setClaimDisabled(true)

      contract
        .claim(
          indexToPass,
          amountToPass,
          formattedProof as ReadonlyArray<`0x${string}`>
        )
        .then((response: any) => {
          toast.promise(response.wait(), {
            loading: "Claiming airdrop",
            success: () => {
              setIsClaimed(true);
              handleCloseModal();
              return "Claim success";
            },
            error: (_) => {
              setClaimDisabled(false)
              return "Claim error";
            },
          });
        })
        .catch(() => {
          console.error("Claim Error");
          setClaimDisabled(false)
        });

      checkUserClaimStatus();
    } catch (error) {
      console.error("Error claiming collabland airdrop tokens:", error);
    }
  };

  const checkUserClaimStatus = async () => {
    if (!contract || !claimData) return;
    try {
      const indexToPass = BigNumber.from(claimData.index);
      const isClaimed = await contract.isClaimed(indexToPass);
      setIsClaimed(isClaimed);
    } catch (error) {
      console.error("Error checking claim status:", error);
    }
  };

  const updateClaimData = async () => {
    const data = await fetch(COLLAB_DROP_API + walletAddress).then((response) => response.json())
    setClaimData(Object.keys(data).length ? data : null)
  };

  if (!isConnected || !claimData || isClaimed) {
    return null;
  }

  return (
    <Content>
      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={1000}
          recycle={false}
          gravity={0.1}
          initialVelocityY={10}
          initialVelocityX={10}
          run={showConfetti}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 10000,
          }}
        />
      )}
      <Toaster />
      {/* BANNER */}
      <BannerContainer>
        <BannerLeft>
          <TokenLogo
            src={`../assets/coins/collab_token.png`}
            alt="token_logo"
          />
          <BannerTextWrapper>
            <BannerHeading>
              Claim
              <HighlightText>$COLLAB</HighlightText>
              Airdrop!
            </BannerHeading>
            <BannerSubText>
              {`Claim and earn Shell Points${
                isMobile ? "!" : " with your wrapped $COLLAB!"
              }`}
            </BannerSubText>
          </BannerTextWrapper>
        </BannerLeft>
        <Button onClick={handleOpenModal}>
          <ButtonText>Claim {!isMobile && "Airdrop"}</ButtonText>
        </Button>
      </BannerContainer>
      {/* MODAL */}
      {modalOpen && (
        <StyledModal
          isWrappedByCollabAirdropBanner={true}
          isVisible={modalOpen}
          onClose={handleCloseModal}
          hideCloseButton={true}
        >
          <ModalContentWrapper>
            <ModalTitleWrapper>
              <Title>You're eligible for $COLLAB!</Title>
            </ModalTitleWrapper>
            <TokenLogo
              src={`../assets/coins/COLLAB.svg`}
              style={{
                margin: "auto",
                paddingTop: "12px",
                paddingBottom: "12px",
                width: "96px",
              }}
            />
            <Text style={{ marginBottom: "12px", fontSize: "16px" }}>
              Breakdown
            </Text>
            <Accordion
              expandable={false}
              title="Government Toucans"
              logo="../assets/coins/TOUCAN.svg"
              tokenAmount={`${formatDisplayShorthand(claimData?.distribution[0] ?? 0, 2)}`}
              endIcon={collabIcon}
            />
            <Accordion
              expandable={false}
              title="Shell Points"
              logo={() => <PointsIcon color="#00BDFF" />}
              tokenAmount={`${formatDisplayShorthand(claimData?.distribution[1] ?? 0, 2)}`}
              endIcon={collabIcon}
            />
            <ModalTextWrapper>
              <Text style={{ lineHeight: "26px" }}>
                You'll receive
                <br />
                <ModalHighlightText>
                  {claimData &&
                    formatDisplay(formatUnits(
                      BigNumber.from(claimData.amount).toString()
                    ), 2)}{<img src={`../assets/coins/shCOLLAB.svg`} style={{position: 'relative', top: '6px', width: '24px', margin: '0 4px'}}/>}
                  shCOLLAB
                </ModalHighlightText>
              </Text>
            </ModalTextWrapper>
            <ModalButton onClick={handleClaim} disabled={claimDisabled}>Claim</ModalButton>
          </ModalContentWrapper>
        </StyledModal>
      )}
    </Content>
  );
};

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0 auto;
  max-width: 500px;
  width: 100%;
  padding: 0px 10px 0px 10px;
`;

const BannerContainer = styled(Box)`
  align-items: center;
  background: #0a0e27;
  border-radius: 20px;
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
  max-width: 100%;
  min-height: 90px;
  padding: 7px 24px 7px 16px;
  position: relative;
  width: 100%;

  ${Media.mobile} {
    margin-bottom: 10px;
    min-height: auto;
    padding: 10px;
  }
`;

const TokenLogo = styled.img`
  height: auto;
  margin-bottom: -8%;
  margin-top: -8%;
  width: 128px;

  ${Media.tablet} {
    width: calc(128px + (160px - 128px) * (100vw - 600px) / (768px - 600px));
  }

  ${Media.mobile} {
    width: min(max(10vw, 64px), 160px);
  }
`;

const AirdropBreakdownTitle = styled.h2`
  color: #ffffff;
  font-size: 24px;
  font-weight: 500;
  text-align: center;
  margin: 10px 0 20px;
`;

const BannerSubText = styled.div`
  align-items: center;
  color: #7d7d97;
  display: flex;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  height: auto;
  line-height: 136%;
  max-width: 431px;
  width: 100%;

  ${Media.smallDesktop} {
    font-size: min(max(1.5vw, 12px), 14px);
  }

  ${Media.mobile} {
    flex-grow: 0;
    flex: none;
    font-size: min(max(1.5vw, 10px), 14px);
    order: 1;
  }
`;
const Button = styled.button`
  align-items: center;
  background-color: #0a0e27;
  border-radius: 16px;
  border: 1px solid #07c0fb;
  box-sizing: border-box;
  display: flex;
  filter: drop-shadow(0px 24px 84px #08081b);
  flex-direction: row;
  gap: 10px;
  height: 47px;
  justify-content: center;
  padding: min(max(1%, 10px), 14px) min(max(3%, 14px), 24px);
  position: relative;
  width: 180px;

  ${Media.tablet} {
    display: flex;
    justify-content: center;
    margin-right: 8px;
  }
`;

const ButtonText = styled.span`
  align-items: center;
  border: none;
  color: #ffffff;
  display: flex;
  font-family: "Inter";
  font-size: min(max(1.6vw, 12px), 16px);
  line-height: min(max(1.9vw, 14px), 19px);
  font-style: normal;
  font-weight: 500;
  text-align: center;
  text-decoration: none;
`;

const BannerLeft = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: nowrap;
  padding-right: 12px;

  ${Media.mobile} {
    justify-content: flex-start;
    align-items: center;
    width: 100%;
  }
`;

const BannerTextWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  flex-basis: 0;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
`;

const BannerHeading = styled.div`
  align-items: center;
  color: #ffffff;
  display: flex;
  font-family: "Inter", sans-serif;
  font-size: min(max(2.5vw, 14px), 24px);
  font-style: normal;
  font-weight: 500;
  height: auto;
  letter-spacing: -0.03em;
  line-height: calc(29px + (24px - 20px) * (100vw - 600px) / (768px - 600px));

  max-width: 470px;
  width: 100%;

  ${Media.tablet} {
    font-size: calc(24px + (18px - 14px) * (100vw - 600px) / (768px - 600px));
  }

  ${Media.mobile} {
    font-size: min(max(2.5vw, 14px), 24px);
  }
`;

const HighlightText = styled.span`
  font-family: "Inter", sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: min(max(2.8vw, 16px), 26px);
  padding-left: 2px;
  padding-right: 2px;
  margin: 0 4px;
  background-image: linear-gradient(90deg, #37dcf2 0%, #07c0fb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  ${Media.tablet} {
    font-size: calc(26px + (20px - 16px) * (100vw - 600px) / (768px - 600px));
  }

  ${Media.mobile} {
    font-size: min(max(2.8vw, 16px), 26px);
  }
`;

const ModalTitleWrapper = styled.div`
  align-items: center;
  display: flex;
  margin: 0px 10px 0px 10px;
  flex-direction: column;
  justify-content: center;
`;

const ModalButton = styled.button`
  align-items: center;
  background: linear-gradient(90.44deg, #37dcf2 0.87%, #07c0fb 100%);
  border-radius: 16px;
  color: #0a0e27;
  display: flex;
  font-size: 18px;
  font-weight: 500;
  flex-shrink: 0;
  height: 64px;
  justify-content: center;
  line-height: 22px;
  margin-top: 40px;
  margin: 0px auto 0px;
  width: 180px;
  svg {
    color: #464659;
    height: 28px;
    margin-right: 8px;
    width: 28px;
  }

  &:not(:disabled) svg path {
    fill: #151530;
  }

  &:disabled {
    background: #0a0e27;
    border: solid 2px #464659;
    color: #464659;
  }

  ${Media.mobile} {
    margin-bottom: 80px;
  }
`;

const ModalTextWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0 12px 12px 12px;
`;

const ModalHighlightText = styled.span`
  -webkit-background-clip: text;
  color: #f6c349;
  font-family: "Inter", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  margin: 0 4px;
  ${Media.mobile} {
    font-size: min(max(2vw, 20px), 26px);
  }
`;

const StyledModal = styled(Modal)`
  color: #00bdff;
  height: 560px;
  svg {
    height: 128px;
    margin: 24px auto 24px;
    width: 128px;
  }

  ${Media.mobile} {
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
    height: 95%;
    margin-top: 10%;
    max-width: 100%;
  }
`;

const Title = styled.h3`
  color: #ffffff;
  font-size: 26px;
  font-weight: 500;
  letter-spacing: -0.03em;
  line-height: 36px;
  text-align: center;

  ${Media.tablet} {
    font-size: calc(30px + (26px - 30px) * (100vw - 600px) / (768px - 600px));
    line-height: calc(36px + (35px - 36px) * (100vw - 600px) / (768px - 600px));
  }

  ${Media.mobile} {
    align-self: stretch;
    flex-grow: 0;
    flex: none;
    font-size: min(max(2.5vw, 24px), 24px);
    line-height: min(max(3vw, 35px), 35px);
    order: 0;
  }
`;

const Text = styled.p`
  color: #7d7d97;
  font-size: 18px;
  font-weight: 300;
  line-height: 24px;
  text-align: center;
`;
